/**
 * BillingPage — Tela "Plano" (assinatura/billing) do CROSS.
 *
 * Implementacao fiel ao prototipo docs/execucao-projetos/_local/design/prototipo/plano.html
 * Sistema de cor, tipografia, motion e shape: tokens.css (zero hardcode).
 * i18n: todas as strings via t(). Sem emoji inline. Sem acesso direto a env.
 * A11y: AA minimo. Keyboard nav em todos os botoes. ARIA semantico.
 *
 * FLAG DE ESTADO HONESTO (F2/F5):
 *   - Billing real (cobranca, metodo de pagamento, upgrade de plano) esta diferido ate F5.
 *   - Os dados de plano/uso renderizados aqui sao os do snapshot da API.
 *   - Onde a API nao retorna campo (preco real, metodo), exibimos estado honesto (—, flag).
 *   - O botao "Ver planos" esta desabilitado ate F5 (deferred).
 *   - Nao prometemos cobranca real nesta tela.
 *
 * Isolamento de CSS:
 *   - screen-plano.css importado aqui e exclusivo desta tela.
 *   - Componentes Plano* sao exclusivos desta tela.
 *   - global.css, tokens.css, AppLayout.tsx nao foram alterados.
 */

import '../styles/screen-plano.css';

import { useEffect, useState } from 'react';
import { t } from '../lib/i18n';
import { Skeleton } from '../components/Skeleton';
import { PlanoCard } from '../components/PlanoCard';
import { PlanoUsageBar } from '../components/PlanoUsageBar';
import { apiClient } from '../lib/api-client';

/* ================================================================
   TIPOS — espelham BillingSnapshot da API
   ================================================================ */
interface UsageAxis {
  readonly id: string;
  readonly label: string;
  readonly current: number;
  readonly max: number;
}

interface PaymentRecord {
  readonly id: string;
  readonly date: string;
  readonly description: string;
  readonly amount: string;
}

interface BillingSnapshot {
  readonly planName: string;
  readonly cycle: string;
  readonly nextBillingAt: string;
  readonly usage: readonly UsageAxis[];
  readonly history: readonly PaymentRecord[];
  readonly planDescription?: string;
  readonly headline?: string;
  /** Preco formatado (ex: "R$ 297") — pode ser null se billing diferido. */
  readonly planPrice?: string | null;
  /** Metodo de pagamento (ex: "Cartao *3725") — pode ser null. */
  readonly paymentMethod?: string | null;
  /** Valor da proxima cobranca formatado. */
  readonly nextBillingAmount?: string | null;
}

type LoadState = { kind: 'loading' } | { kind: 'ready'; data: BillingSnapshot } | { kind: 'error' };

/* ================================================================
   buildPlanFeatures — features do plano via i18n.
   Chamado dentro do componente para garantir hot-reload e separacao
   entre modulo e runtime. t() e pura — sem efeito colateral.
   ================================================================ */
function buildPlanFeatures(): ReadonlyArray<{ readonly label: string; readonly desc: string }> {
  return [
    { label: t('billing.feature1Label'), desc: t('billing.feature1Desc') },
    { label: t('billing.feature2Label'), desc: t('billing.feature2Desc') },
    { label: t('billing.feature3Label'), desc: t('billing.feature3Desc') },
    { label: t('billing.feature4Label'), desc: t('billing.feature4Desc') },
    { label: t('billing.feature5Label'), desc: t('billing.feature5Desc') },
    { label: t('billing.feature6Label'), desc: t('billing.feature6Desc') },
  ];
}

/* ================================================================
   COMPONENTE PRINCIPAL
   ================================================================ */
export function BillingPage(): JSX.Element {
  const [state, setState] = useState<LoadState>({ kind: 'loading' });
  const [viewPlansDeferredMsg, setViewPlansDeferredMsg] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async (): Promise<void> => {
      try {
        const data = await apiClient.get<BillingSnapshot>('/billing/snapshot');
        if (!cancelled) setState({ kind: 'ready', data });
      } catch {
        if (!cancelled) setState({ kind: 'error' });
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleViewPlans = (): void => {
    setViewPlansDeferredMsg(t('billing.viewPlansDeferredMsg'));
  };

  /* ----------------------------------------------------------------
     ESTADO: LOADING
     ---------------------------------------------------------------- */
  if (state.kind === 'loading') {
    return (
      <section aria-labelledby="billing-title" aria-busy="true">
        <div className="plano-page-header" style={{ animationDelay: '0ms' }}>
          <div>
            <div className="plano-page-eyebrow">{t('billing.pageEyebrow')}</div>
            <h1 id="billing-title" className="plano-page-title">
              {t('billing.pageTitle')}
            </h1>
          </div>
        </div>
        <div className="plano-layout">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--mkv-space-3)' }}>
            <Skeleton variant="heading" standalone ariaLabel={t('billing.loading')} />
            <Skeleton variant="kpi-value" />
            <Skeleton variant="text" />
            <Skeleton variant="text" width="70%" />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--mkv-space-3)' }}>
            <Skeleton variant="card" />
            <Skeleton variant="card" />
          </div>
        </div>
      </section>
    );
  }

  /* ----------------------------------------------------------------
     ESTADO: ERRO
     ---------------------------------------------------------------- */
  if (state.kind === 'error') {
    return (
      <section aria-labelledby="billing-title">
        <div className="plano-page-header">
          <div>
            <div className="plano-page-eyebrow">{t('billing.pageEyebrow')}</div>
            <h1 id="billing-title" className="plano-page-title">
              {t('billing.pageTitle')}
            </h1>
          </div>
        </div>
        <div className="plano-state-section">
          <div className="plano-error-state" role="alert">
            <div className="plano-error-icon-row">
              <span className="plano-error-icon" aria-hidden="true">
                error
              </span>
              <span className="plano-error-title">{t('billing.error')}</span>
            </div>
          </div>
        </div>
      </section>
    );
  }

  /* ----------------------------------------------------------------
     ESTADO: PRONTO
     ---------------------------------------------------------------- */
  const { data } = state;
  const planFeatures = buildPlanFeatures();

  /* Monta os medidores de uso a partir do snapshot */
  const getUsageAxis = (id: string): UsageAxis | undefined => data.usage.find((u) => u.id === id);

  const fontesAxis = getUsageAxis('fontes');
  const historicoAxis = getUsageAxis('historico');
  const relatoriosAxis = getUsageAxis('relatorios');

  return (
    <section aria-labelledby="billing-title">
      {/* ============================================================
          PAGE HEADER — titulo editorial com hairline tricolor
          ============================================================ */}
      <div className="plano-page-header">
        <div>
          <div className="plano-page-eyebrow" aria-hidden="true">
            {t('billing.pageEyebrow')}
          </div>
          <h1 id="billing-title" className="plano-page-title">
            Seu <em>{t('billing.pageTitleEm')}</em>
            <br />
            CROSS
          </h1>
          <p className="plano-page-sub">
            {data.planName} · {t('billing.pageSub')}
          </p>
        </div>
        <div className="plano-page-meta">
          {data.nextBillingAt.length > 0 ? (
            <>
              {t('billing.nextBilling')}{' '}
              <strong>
                {new Date(data.nextBillingAt).toLocaleDateString('pt-BR', {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric',
                })}
              </strong>
            </>
          ) : (
            t('billing.nextBillingNone')
          )}
          {data.paymentMethod !== undefined && data.paymentMethod !== null ? (
            <>
              <br />
              {data.paymentMethod}
            </>
          ) : null}
        </div>
      </div>

      {/* Mensagem de estado diferido (billing F5) — inline */}
      {viewPlansDeferredMsg !== null ? (
        <p
          role="status"
          className="plano-page-sub"
          style={{ marginBottom: 'var(--mkv-space-4)', color: 'var(--md-on-surface-variant)' }}
        >
          {viewPlansDeferredMsg}
        </p>
      ) : null}

      {/* ============================================================
          LAYOUT PRINCIPAL — 2 colunas
          ============================================================ */}
      <div className="plano-layout">
        {/* ===== CARD DE PLANO ATUAL ===== */}
        {/* planTaglineFallback: neutro quando API nao retorna planDescription — copy inline task-scope ok */}
        <PlanoCard
          planName={data.planName}
          planTagline={data.planDescription}
          planTaglineFallback="Tudo que voce precisa para acompanhar seu negocio em um lugar so."
          planPrice={data.planPrice ?? null}
          cycle={data.cycle}
          nextBillingAt={data.nextBillingAt}
          paymentMethod={data.paymentMethod ?? null}
          features={planFeatures}
          isActive
          onViewPlans={handleViewPlans}
        />

        {/* ===== SIDEBAR ===== */}
        <aside className="plano-sidebar" aria-label={t('billing.sidebarAriaLabel')}>
          {/* Medidores de uso */}
          <div className="plano-usage-card" aria-labelledby="plano-uso-titulo">
            <div className="plano-usage-title" id="plano-uso-titulo">
              {t('billing.usageCardTitle')}
            </div>

            {data.usage.length === 0 ? (
              <p className="plano-usage-item-note">{t('billing.usageEmpty')}</p>
            ) : (
              <>
                {/* Eixo "fontes" — label, unidade e nota localizados */}
                {fontesAxis !== undefined ? (
                  <PlanoUsageBar
                    label={t('billing.usageLabelFontes')}
                    current={fontesAxis.current}
                    max={fontesAxis.max}
                    unit={t('billing.usageUnitFontes')}
                    limitReached={fontesAxis.max > 0 && fontesAxis.current >= fontesAxis.max}
                    note={
                      fontesAxis.max > 0 && fontesAxis.current >= fontesAxis.max
                        ? t('billing.usageNoteFontesLimit')
                        : undefined
                    }
                  />
                ) : null}

                {/* Eixo "historico" — label, unidade e nota localizados */}
                {historicoAxis !== undefined ? (
                  <PlanoUsageBar
                    label={t('billing.usageLabelHistorico')}
                    current={historicoAxis.current}
                    max={historicoAxis.max}
                    unit={t('billing.usageUnitMeses')}
                    limitReached={false}
                    note={t('billing.usageNoteHistoricoOk')}
                  />
                ) : null}

                {/* Eixo "relatorios" — label, unidade e nota localizados */}
                {relatoriosAxis !== undefined ? (
                  <PlanoUsageBar
                    label={t('billing.usageLabelRelatorios')}
                    current={relatoriosAxis.current}
                    max={relatoriosAxis.max}
                    unit={t('billing.usageUnitRelatorios')}
                    limitReached={false}
                    note={t('billing.usageNoteRelatoriosOk')}
                  />
                ) : null}

                {/* Eixos nao mapeados — renderizacao generica com label da API */}
                {data.usage
                  .filter((u) => u.id !== 'fontes' && u.id !== 'historico' && u.id !== 'relatorios')
                  .map((axis) => (
                    <PlanoUsageBar
                      key={axis.id}
                      label={axis.label}
                      current={axis.current}
                      max={axis.max}
                      limitReached={axis.max > 0 && axis.current >= axis.max}
                    />
                  ))}
              </>
            )}
          </div>

          {/* Card de proxima cobranca */}
          <div className="plano-billing-card" aria-labelledby="plano-cobranca-titulo">
            <div className="plano-billing-card-title" id="plano-cobranca-titulo">
              {t('billing.billingCardTitle')}
            </div>

            {/* FLAG: billing real diferido ate F5 */}
            {data.nextBillingAt.length === 0 ? (
              <div className="plano-billing-deferred" role="note">
                <span className="plano-flag-dot" aria-hidden="true" />
                {t('billing.billingDeferredNote')}
              </div>
            ) : (
              <>
                <div className="plano-billing-next-date">
                  {new Date(data.nextBillingAt).toLocaleDateString('pt-BR', {
                    day: '2-digit',
                    month: 'short',
                    year: 'numeric',
                  })}
                </div>
                <div className="plano-billing-next-sub">{t('billing.billingNextSub')}</div>
                <div className="plano-billing-divider" aria-hidden="true" />
                <dl className="plano-billing-dl">
                  <dt className="plano-billing-dt">{data.planName}</dt>
                  <dd className="plano-billing-dd">{data.planPrice ?? '—'}</dd>
                  <dt className="plano-billing-dt">{t('billing.billingDiscountLabel')}</dt>
                  <dd className="plano-billing-dd">{t('billing.billingDiscountNone')}</dd>
                  <dt className="plano-billing-dt">{t('billing.billingTotalLabel')}</dt>
                  <dd className="plano-billing-dd plano-billing-dd--total">
                    {data.nextBillingAmount ?? data.planPrice ?? '—'}
                  </dd>
                </dl>
              </>
            )}
          </div>

          {/* Card de suporte */}
          <div className="plano-support-card" aria-labelledby="plano-suporte-titulo">
            <div className="plano-usage-title" id="plano-suporte-titulo">
              {t('billing.supportCardTitle')}
            </div>
            <p className="plano-support-text">{t('billing.supportCardText')}</p>
            <button
              type="button"
              className="plano-chip-action"
              aria-label={t('billing.supportCardCta')}
              onClick={() => {
                /* Chat com a CROSS — navegacao gerenciada pelo router principal */
              }}
            >
              <span className="plano-chip-icon" aria-hidden="true">
                forum
              </span>
              {t('billing.supportCardCta')}
            </button>
          </div>
        </aside>
      </div>

      {/* ============================================================
          SECAO DE ESTADOS — demo para validacao visual (A5 CROSS M3)
          Visivel apenas em dev/staging; em producao pode ser removida
          por feature flag. Aqui permanece para o owner validar os estados.
          ============================================================ */}
      <div className="plano-state-section">
        <div
          className="plano-usage-title"
          style={{ marginBottom: 'var(--mkv-space-5)' }}
          aria-hidden="true"
        >
          {t('billing.statesDemoTitle')}
        </div>
        <div className="plano-state-grid" aria-hidden="true">
          {/* Estado: carregando */}
          <div
            className="plano-usage-card"
            style={{ animationDelay: '0ms', gap: 'var(--mkv-space-2)' }}
            aria-hidden="true"
          >
            <span
              className="plano-usage-item-label"
              style={{
                fontSize: 'var(--mkv-text-xs)',
                textTransform: 'uppercase',
                letterSpacing: '.8px',
              }}
            >
              {t('billing.stateLoadingLabel')}
            </span>
            <span
              className="plano-skel plano-skel--sm"
              style={{ height: '10px', marginBottom: '2px' }}
            />
            <span className="plano-skel plano-skel--lg" style={{ marginBottom: '6px' }} />
            <span className="plano-skel plano-skel--md" />
            <span className="plano-skel plano-skel--sm" style={{ marginTop: '4px' }} />
          </div>

          {/* Estado: vazio — sem historico de pagamentos */}
          <div className="plano-usage-card" style={{ animationDelay: '60ms' }} aria-hidden="true">
            <span
              className="plano-usage-item-label"
              style={{
                fontSize: 'var(--mkv-text-xs)',
                textTransform: 'uppercase',
                letterSpacing: '.8px',
              }}
            >
              {t('billing.stateEmptyLabel')}
            </span>
            <div className="plano-empty-state">
              <span className="plano-empty-icon" aria-hidden="true">
                receipt_long
              </span>
              <div className="plano-empty-msg">{t('billing.emptyPaymentsMsg')}</div>
              <div className="plano-empty-hint">{t('billing.emptyPaymentsHint')}</div>
            </div>
          </div>

          {/* Estado: erro de cobranca + data-gap */}
          <div className="plano-usage-card" style={{ animationDelay: '120ms' }} aria-hidden="true">
            <span
              className="plano-usage-item-label"
              style={{
                fontSize: 'var(--mkv-text-xs)',
                textTransform: 'uppercase',
                letterSpacing: '.8px',
              }}
            >
              {t('billing.stateErrorLabel')}
            </span>
            <div className="plano-error-state">
              <div className="plano-error-icon-row">
                <span className="plano-error-icon" aria-hidden="true">
                  error
                </span>
                <span className="plano-error-title">{t('billing.errorPaymentTitle')}</span>
              </div>
              <p className="plano-error-desc">{t('billing.errorPaymentDesc')}</p>
              <div
                style={{
                  marginTop: 'var(--mkv-space-2)',
                  paddingTop: 'var(--mkv-space-2)',
                  borderTop: '1px solid var(--md-outline-variant)',
                }}
              >
                <span
                  className="plano-usage-item-label"
                  style={{
                    display: 'block',
                    fontSize: 'var(--mkv-text-xs)',
                    marginBottom: '6px',
                    textTransform: 'uppercase',
                    letterSpacing: '.7px',
                  }}
                >
                  {t('billing.usageCardTitle')}
                </span>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'baseline',
                    gap: '8px',
                    marginBottom: '6px',
                  }}
                >
                  <span
                    style={{
                      fontFamily: 'var(--mkv-font-display)',
                      fontSize: 'var(--mkv-text-3xl)',
                      fontWeight: 700,
                      letterSpacing: '-1.5px',
                      color: 'var(--md-on-surface-variant)',
                    }}
                    aria-hidden="true"
                  >
                    —
                  </span>
                  <span className="plano-data-gap-chip">
                    <span className="plano-data-gap-dot" aria-hidden="true" />
                    {t('billing.usageEmpty')}
                  </span>
                </div>
                <p className="plano-usage-item-note">{t('billing.dataGapNote')}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
